import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, RotateCcw, Calendar, TrendingDown, CheckCircle2, Shield, Lock, Flame } from 'lucide-react';
import Header from '../components/Header';
import MealPlan from '../components/MealPlan';
import MealTracker from '../components/MealTracker';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const [quiz, setQuiz] = useState(null);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login?next=/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [q, s] = await Promise.all([
          axios.get(`${API}/quiz/latest`, { withCredentials: true }),
          axios.get(`${API}/payment/my-subscription`, { withCredentials: true }),
        ]);
        setQuiz(q.data);
        setSub(s.data);
      } catch (e) {
        // ignore
      } finally {
        setBusy(false);
      }
    })();
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0d0e10] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1f2024] loader-ring rounded-full" />
      </div>
    );
  }

  const answers = quiz?.answers || {};
  const currentWeight = parseFloat(answers?.measurements?.weight);
  const targetWeight = parseFloat(answers?.targetWeight ?? answers?.['target-weight']);
  const unit = answers?.measurements?.weightUnit || 'lbs';
  const loss = currentWeight && targetWeight ? Math.max(0, currentWeight - targetWeight).toFixed(1) : null;
  const mealGoal = currentWeight && targetWeight && targetWeight > currentWeight ? 'gain' : 'loss';

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 md:px-8 py-8">
        <div className="max-w-4xl mx-auto" data-testid="dashboard-main">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-[#ff4e15]" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#ff4e15] flex items-center justify-center text-xl font-black text-white">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <div>
                <div className="text-sm text-white/60">{t('welcome')}</div>
                <div className="heading-font text-3xl font-black">{user.name?.toUpperCase()}</div>
                <div className="text-xs text-white/50">{user.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#ff4e15] rounded-sm text-sm font-semibold text-white hover:bg-[#ff6033] transition-colors"
                  data-testid="dash-admin-btn"
                >
                  <Shield size={14} /> {t('adminPanel')}
                </button>
              )}
              <button
                onClick={async () => { await logout(); navigate('/'); }}
                className="flex items-center gap-2 px-4 py-2 border border-[#2a2b2f] rounded-sm text-sm hover:border-[#ff4e15] hover:text-[#ff4e15] transition-colors"
                data-testid="dash-logout-btn"
              >
                <LogOut size={14} /> {t('signOut')}
              </button>
            </div>
          </div>

          {/* Subscription card */}
          <div className="mt-8 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold">{t('yourSubscription')}</h2>
              {sub && sub.status === 'active' ? (
                <span className="text-xs bg-[#ff4e15]/20 text-[#ff4e15] px-3 py-1 rounded-sm font-semibold flex items-center gap-1">
                  <CheckCircle2 size={12} /> {t('active')}
                </span>
              ) : (
                <span className="text-xs bg-[#2a2b2f] text-white/60 px-3 py-1 rounded-sm font-semibold">{t('none')}</span>
              )}
            </div>
            {sub ? (
              <div className="space-y-1">
                <div className="text-2xl heading-font font-black text-[#ff4e15]">{sub.plan_name}</div>
                <div className="text-sm text-white/70 flex items-center gap-2">
                  <Calendar size={14} /> {t('renewsOn')} {new Date(sub.expires_at).toLocaleDateString()}
                </div>
                {sub.mock && (
                  <div className="text-xs text-yellow-500/80 mt-2">{t('paymentMock')}</div>
                )}
              </div>
            ) : (
              <div className="text-white/70 text-sm">
                {t('noActivePlan')}{' '}
                <button onClick={() => navigate('/')} className="text-[#ff4e15] hover:underline">
                  {t('takeQuizToGetOne')}
                </button>
              </div>
            )}
          </div>

          {/* Quiz result summary */}
          <div className="mt-6 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{t('yourPlan')}</h2>
              <button
                onClick={() => navigate('/')}
                className="text-xs text-white/60 hover:text-[#ff4e15] flex items-center gap-1"
              >
                <RotateCcw size={12} /> {t('retakeQuizShort')}
              </button>
            </div>
            {busy ? (
              <div className="text-white/50">{t('loading')}</div>
            ) : quiz ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Stat label={t('ageGroup')} value={answers.ageGroup} />
                <Stat label={t('gender')} value={answers.gender} />
                <Stat label={t('bodyType')} value={answers['body-type']} />
                <Stat label={t('goal')} value={answers['goal-body']} />
                <Stat label={t('currentWeight')} value={currentWeight ? `${currentWeight} ${unit}` : '—'} />
                <Stat label={t('targetWeight')} value={targetWeight ? `${targetWeight} ${unit}` : '—'} />
                {loss && (
                  <div className="col-span-2 md:col-span-3 mt-2 p-4 bg-[#1f1210] border border-[#ff4e15]/40 rounded-sm flex items-center gap-3">
                    <TrendingDown size={20} className="text-[#ff4e15]" />
                    <div>
                      <div className="text-xs text-white/60">{t('targetReduction')}</div>
                      <div className="heading-font text-2xl font-black text-[#ff4e15]">{loss} {unit}</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-white/70 text-sm">
                {t('noQuizOnFile')}{' '}
                <button onClick={() => navigate('/')} className="text-[#ff4e15] hover:underline">
                  {t('takeTheQuiz')}
                </button>
              </div>
            )}
          </div>

          {/* 30-Day Meal Plan — locked behind active subscription */}
          <div className="mt-6">
            {sub && sub.status === 'active' ? (
              <>
                <MealTracker />
                <div className="mt-6">
                  <MealPlan goal={mealGoal} answers={answers} />
                </div>
              </>
            ) : (
              <div className="relative bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none opacity-25 blur-sm select-none">
                  <MealPlan goal={mealGoal} answers={answers} />
                </div>

                <div className="relative flex flex-col items-center text-center py-14 px-4">
                  <div className="w-16 h-16 rounded-full bg-[#ff4e15]/15 border-2 border-[#ff4e15] flex items-center justify-center">
                    <Lock size={28} className="text-[#ff4e15]" />
                  </div>
                  <h2 className="heading-font text-2xl md:text-3xl font-black mt-4 tracking-wide">
                    {t('unlockPlan')}
                  </h2>
                  <p className="mt-2 text-white/70 max-w-md text-sm">
                    {t('unlockPlanSub')}
                  </p>
                  <ul className="mt-4 text-xs text-white/60 space-y-1 text-left">
                    <li className="flex items-center gap-2"><Flame size={12} className="text-[#ff4e15]" /> {t('unlockFeature1')}</li>
                    <li className="flex items-center gap-2"><Flame size={12} className="text-[#ff4e15]" /> {t('unlockFeature2')}</li>
                    <li className="flex items-center gap-2"><Flame size={12} className="text-[#ff4e15]" /> {t('unlockFeature3')}</li>
                    <li className="flex items-center gap-2"><Flame size={12} className="text-[#ff4e15]" /> {t('unlockFeature4')}</li>
                    <li className="flex items-center gap-2"><Flame size={12} className="text-[#ff4e15]" /> {t('unlockFeature5')}</li>
                    <li className="flex items-center gap-2"><Flame size={12} className="text-[#ff4e15]" /> {t('unlockFeature6')}</li>
                  </ul>
                  <button
                    onClick={() => navigate('/plan')}
                    className="mt-6 px-6 py-3 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] transition-colors pulse-orange"
                    data-testid="dash-purchase-btn"
                  >
                    {t('purchasePlan')}
                  </button>
                  <p className="mt-3 text-[10px] text-white/40">
                    {t('startsAt')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="p-3 bg-[#141518] border border-[#2a2b2f] rounded-sm">
    <div className="text-xs text-white/50 uppercase tracking-wide">{label}</div>
    <div className="text-sm font-semibold text-white capitalize mt-1">{value || '—'}</div>
  </div>
);

export default Dashboard;
